import React, { useState, useEffect, useRef } from 'react';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../../utils/breadcrumbUtils';
import { Perfil } from '../../../model/Usuario/Perfil';
import { PerfilViewModel } from '../../../viewmodel/usuario/PerfilViewModel';
import UsuarioViewModel from '../../../viewmodel/usuario/UsuarioViewModel';
import FiltrosUsuarios from './components/FiltrosUsuarios';
import TabelaUsuarios from './components/TabelaUsuarios';
import PaginacaoUsuarios from './components/PaginacaoUsuarios';
import CriarPerfilModal from './CriarPerfilModal';
import './index.css';
import HelpDrawerUsuarios from './components/HelpDrawerUsuarios';
import { BsQuestionCircle } from 'react-icons/bs';

const GerenciarPerfis: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [usuarios, setUsuarios] = useState({ content: [], pageMetadata: { totalElements: 0, totalPages: 0, size: 10, number: 0 } });
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ nome: '', email: '', matricula: '', perfil: '' });
  const [currentPage, setCurrentPage] = useState(0);
  const [showCriarPerfilModal, setShowCriarPerfilModal] = useState(false);
  const [perfisDisponiveis, setPerfisDisponiveis] = useState<Perfil[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [showHelpDrawer, setShowHelpDrawer] = useState(false);
  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs.withoutProcessos());
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      fetchPerfis();
      fetchUsuarios();
    }
  }, [initialized]);

  // Filtros com debounce
  useEffect(() => {
    if (!initialized) return;
    
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchUsuarios(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filtros.nome, filtros.email, filtros.matricula, filtros.perfil]);

  const fetchPerfis = async () => {
    try {
      const perfis = await PerfilViewModel.obterPerfisParaEditarUsuario();
      perfis.push({nome: 'sem perfil', permissoes: [], id: ''});
      setPerfisDisponiveis(perfis);
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
    }
  };

  const fetchUsuarios = async (page = currentPage) => {
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }
    
    const fetchPromise = (async () => {
      setLoading(true);
      try {
        const filtrosLimpos: any = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v !== ''));
        
        const result = await UsuarioViewModel.searchUsuarios(filtrosLimpos, page, 10);
        setUsuarios(result);
      } catch (error) {
        console.error('Erro:', error);
        setUsuarios({ content: [], pageMetadata: { totalElements: 0, totalPages: 0, size: 10, number: page } });
      } finally {
        setLoading(false);
        fetchPromiseRef.current = null;
      }
    })();
    
    fetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  };

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchUsuarios(newPage);
  };

  const handleCreatePerfil = (newPerfil: Perfil) => {
    setPerfisDisponiveis((prevPerfis) => [...prevPerfis, newPerfil]);
  };

  return (
    <HelpDrawerUsuarios isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
    <div className="usuarios-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Gerenciar Usuários</h2>
        <button
          className="btn-pagination"
          onClick={() => setShowHelpDrawer(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
        >
          <BsQuestionCircle /> Ajuda
        </button>
      </div>
      
      <FiltrosUsuarios 
        filtros={filtros}
        onFiltroChange={handleFiltroChange}
      />

      <TabelaUsuarios 
        usuarios={usuarios.content}
        loading={loading}
        onUsuarioUpdated={() => fetchUsuarios(currentPage)}
        perfisDisponiveis={perfisDisponiveis}
      />
      
      <div className="pagination-container" style={{ justifyContent: 'space-between' }}>
        <PaginacaoUsuarios 
          pageMetadata={usuarios.pageMetadata}
          onPageChange={handlePageChange}
        />
        
        <button 
          className="btn-pagination" 
          style={{ 
            backgroundColor: '#28a745', 
            color: 'white', 
            border: '1px solid #28a745',
            fontWeight: '500'
          }}
          onClick={() => setShowCriarPerfilModal(true)}
        >
          + Criar Perfil
        </button>
      </div>

      <CriarPerfilModal
        show={showCriarPerfilModal}
        handleClose={() => setShowCriarPerfilModal(false)}
        perfisDisponiveis={perfisDisponiveis}
        setPerfisDisponiveis={setPerfisDisponiveis}
      />
    </div>
    </HelpDrawerUsuarios>
  );
};

export default GerenciarPerfis;
